import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import GenericDashboard from "../GenericDashboard/GenericDashboard";
import APIService from "../../Service/APIService";
import { Button, Input, Modal, Flex, Spin } from "antd";
import {
  PlusOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import TitleBar from "../../common/title-bar/title-bar";
import { colors } from "../../theme/colors";
import { useAuth } from '../AuthProvider.js';
import { hasPermission } from "../../Config/permissions.js";
import CommonButton from "../../common/button/button.js";

const isActiveFlag = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  String(value).toLowerCase() === "true";

const isInactiveFlag = (value) =>
  value === false ||
  value === 0 ||
  value === "0" ||
  String(value).toLowerCase() === "false";

const normalizeManufacturers = (rows = []) =>
  rows.map((manufacturer) => ({
    ...manufacturer,
    IsActive:
      manufacturer?.IsActive === undefined || manufacturer?.IsActive === null
        ? true
        : manufacturer.IsActive,
  }));

const ManufacturerDashboard = () => {
  const { user, isReady } = useAuth();
  const [allManufacturerData, setAllManufacturerData] = React.useState([]);
  const [userType, setUserType] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [searchKeyword, setSearchKeyword] = React.useState("");
  const [selectedSegment, setSelectedSegment] = useState("Active");

  const [showNewAccountModal, setShowNewAccountModal] = React.useState(false);
  const navigate = useNavigate();

  const handleCancel = () => {
    console.log("Clicked cancel button");
    setShowNewAccountModal(false);
  };

  const handleInputChange = (e) => {
    setManufacturerName(e.target.value);
  };

  const [confirmLoading, setConfirmLoading] = React.useState(false);

  const [manufacturerName, setManufacturerName] = useState("");

  const handleOk = async () => {
    const trimmedManufacturerName = manufacturerName.trim();
    if (!trimmedManufacturerName) {
      return;
    }
    console.log("Manufacturer Name: ", trimmedManufacturerName);
    setConfirmLoading(true);
    setConfirmLoading(false);
    setShowNewAccountModal(false);
    setManufacturerName("");
    navigate(
      `/Manufacturer?tab=details&manufacturerName=${encodeURIComponent(
        trimmedManufacturerName
      )}`
    );
  };

  React.useEffect(() => {
    if (!isReady) return;
    console.log("Manufacturer Dashboard component mounted");
    const userData = user;
    setUserType(userData?.Type || "");
    if (userData?.Type === "Account") {
      window.location.href = "/";
      return;
    }

    const fetchManufacturers = async () => {
      setLoading(true);
      try {
        const apiServe = new APIService();
        const response = await apiServe.getDataFromBackend("getmanufacturers");
        console.log("Get Data Response: ", response?.data?.data);
        const data = normalizeManufacturers(response?.data?.data || []);
        console.log("Manufacturer Data: ", data);
        setAllManufacturerData(data);
      } catch (error) {
        console.error("Failed to fetch manufacturers dashboard data:", error);
        setAllManufacturerData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchManufacturers();
  }, [isReady, user]);

  const handleSegmentChange = (value) => {
    setSelectedSegment(value);
  };

  const filteredManufacturerData = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    return allManufacturerData.filter((manufacturer) => {
      const matchesStatus =
        selectedSegment === "Active"
          ? isActiveFlag(manufacturer?.IsActive)
          : isInactiveFlag(manufacturer?.IsActive);
      const matchesSearch =
        keyword === "" ||
        manufacturer?.ManufacturerName?.toLowerCase()?.includes(keyword);

      return matchesStatus && matchesSearch;
    });
  }, [allManufacturerData, searchKeyword, selectedSegment]);

  const renderSearchAndActions = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: "10px",
      }}
    >
      <Input.Search
        placeholder="Search"
        allowClear
        style={{ height: "36px" }}
        onChange={(e) => {
          if (e.target.value === "") {
            setSearchKeyword("");
          }
        }}
        onSearch={(value) => setSearchKeyword(value || "")}
      />
      {userType === "Admin" && hasPermission(user, "manage_manufacturers.add") && (
        <Button
          type="primary"
          onClick={() => setShowNewAccountModal(true)}
          style={{ background: colors["primaryColor"] }}
          icon={<PlusOutlined />}
          iconPosition="start"
        >
          New
        </Button>
      )}
    </div>
  );

  return (
    <div>
      <TitleBar
        label="Manufacturers"
        invoiceNumber={`${filteredManufacturerData?.length}`}
        filterBarComponents={
          <>
            <CommonButton
              isActive={selectedSegment === "Active"}
              variant="timeframe"
              onClick={() => handleSegmentChange("Active")}
            >
              Active
            </CommonButton>
            <CommonButton
              isActive={selectedSegment === "Inactive"}
              variant="timeframe"
              onClick={() => handleSegmentChange("Inactive")}
            >
              Inactive
            </CommonButton>
          </>
        }
        rightFilterBarComponents={renderSearchAndActions()}
      />

      {loading && (
        <Flex align="center" gap="middle">
          <Spin
            indicator={
              <LoadingOutlined
                style={{
                  fontSize: 48,
                }}
                spin
              />
            }
            fullscreen
          />
        </Flex>
      )}
      {filteredManufacturerData && !loading && (
        <>
          <GenericDashboard
            data={filteredManufacturerData}
            screen="ManufacturerDashboard"
          />
          <Modal
            title="New Manufacturer"
            open={showNewAccountModal}
            onOk={handleOk}
            confirmLoading={confirmLoading}
            onCancel={handleCancel}
            centered={true}
            footer={false}
          >
            <label htmlFor="ManufacturerName" className="form-input-label">
              Manufacturer Name
            </label>
            <Input
              id="ManufacturerName"
              name="ManufacturerName"
              placeholder="Enter Manufacturer Name"
              value={manufacturerName}
              onChange={handleInputChange}
            />
            <p>
              You will be redirected to fill out the remaining Manufacturer
              details once you have added the Manufacturer.
            </p>
            <Button
              type="primary"
              onClick={handleOk}
              disabled={!manufacturerName.trim()}
              style={{ background: "#27a907" }}
            >
              Add Manufacturer
            </Button>
          </Modal>
        </>
      )}
    </div>
  );
};

export default ManufacturerDashboard;
